/**
 * LoadingAndError Component
 * 
 * Component for displaying loading and error states for data fetching operations.
 * Provides visual feedback during data loading and error handling.
 * 
 * Features:
 * - Loading overlay with spinner animation
 * - Error message display with retry button
 * - Accessibility attributes (ARIA roles and live regions)
 * - Conditional rendering (returns null when neither loading nor error)
 * - Page reload on retry button click
 * 
 * Used in both office employee and construction worker data display components.
 * 
 * @component
 */

import React from 'react';

/**
 * Props interface for LoadingAndError component.
 */
interface LoadingAndErrorProps {
  loading: boolean;            // Loading state indicator
  error: string | null;        // Error message to display (null if no error)
}

/**
 * LoadingAndError Component
 * 
 * Renders loading overlay or error message based on props.
 * Returns null when neither loading nor error states are active.
 * 
 * @param props - Component props
 * @returns React functional component or null
 */
export const LoadingAndError: React.FC<LoadingAndErrorProps> = ({
  loading,
  error
}) => {
  /**
   * Loading State - Displays overlay with spinner.
   * 
   * Shows a loading overlay with spinner animation when data is being fetched.
   * Includes accessibility attributes for screen readers.
   */
  if (loading) {
    return (
      <div className="data-loading-overlay" role="status" aria-live="polite" aria-busy="true">
        {/* Spinner animation */}
        <div className="data-spinner"></div>
      </div>
    );
  }

  /**
   * Error State - Displays error message with retry button.
   * 
   * Shows error message with ❌ icon and a retry button that reloads the page.
   * Allows users to retry the failed operation.
   */
  if (error) {
    return (
      <div className="error-message">
        {/* Error icon and message text */}
        <p>❌ {error}</p>
        {/* Retry button - Reloads the page to retry the operation */}
        <button onClick={() => window.location.reload()}>再試行</button>
      </div>
    );
  }

  /**
   * No State - Returns null when neither loading nor error.
   * 
   * When data is successfully loaded and no errors exist,
   * the component returns null to hide itself.
   */
  return null;
}; 